import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/context/LangContext";
import { CartProvider } from "@/context/CartContext";
import Layout from "@/components/Layout";
import CookieBanner from "@/components/CookieBanner";
import SEO from "@/components/SEO";

// Home is the most likely first render — keep it eager for best LCP
import HomePage from "@/pages/HomePage";

// Everything else is code-split (React.lazy) — reduces initial JS on the home route,
// cuts unused JavaScript and Total Blocking Time significantly.
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentPages").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentPages").then(m => ({ default: m.PaymentCancelPage })));
const HowItWorksPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.HowItWorksPage })));
const FAQPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.FAQPage })));
const AboutPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.ContactPage })));
const TrackOrderPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.TrackOrderPage })));
const LegalPage = lazy(() => import("@/pages/StaticPages").then(m => ({ default: m.LegalPage })));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading" aria-busy="true">
    <div className="w-8 h-8 rounded-full border-2 border-slate-700 border-t-blue-500 animate-spin" />
  </div>
);

function App() {
  return (
    <LangProvider>
      <CartProvider>
        <BrowserRouter>
          <SEO />
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <Routes>
            <Route path="/admin/login" element={<Suspense fallback={<RouteFallback />}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><AdminDashboard /></Suspense>} />
            <Route path="*" element={
              <Layout>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/boutique" element={<ShopPage />} />
                    <Route path="/fonctionnement" element={<HowItWorksPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/a-propos" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/suivi" element={<TrackOrderPage />} />
                    <Route path="/panier" element={<CartPage />} />
                    <Route path="/paiement" element={<CheckoutPage />} />
                    <Route path="/payment/success" element={<PaymentSuccessPage />} />
                    <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                    <Route path="/politique/:slug" element={<LegalPage />} />
                  </Routes>
                </Suspense>
              </Layout>
            } />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </LangProvider>
  );
}

export default App;
